import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Alert, AlertTitle, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import JSZip from 'jszip';

function FdiRepositoryUploader({ apiBaseUrl }) {
  const { instance, accounts } = useMsal();
  const [eddFile, setEddFile] = useState(null);
  const [packageFile, setPackageFile] = useState(null);
  const [devinfoFile, setDevinfoFile] = useState(null);
  const [eddLoading, setEddLoading] = useState(false);
  const [packageLoading, setPackageLoading] = useState(false);
  const [devinfoLoading, setDevinfoLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [eddProgress, setEddProgress] = useState(0);
  const [packageProgress, setPackageProgress] = useState(0);
  const [devinfoProgress, setDevinfoProgress] = useState(0);
  const [eddProtocol, setEddProtocol] = useState('');
  const [eddManufacturerId, setEddManufacturerId] = useState('');
  const [eddDeviceType, setEddDeviceType] = useState('');
  const [eddDeviceRevision, setEddDeviceRevision] = useState('');
  const [eddRecordStatus, setEddRecordStatus] = useState('active'); // New state for EDD recordStatus
  const [devinfoProtocol, setDevinfoProtocol] = useState('');
  const [devinfoManufacturerId, setDevinfoManufacturerId] = useState('');
  const [devinfoDeviceType, setDevinfoDeviceType] = useState('');
  const [devinfoDeviceRevision, setDevinfoDeviceRevision] = useState('');
  const [devinfoRecordStatus, setDevinfoRecordStatus] = useState('active'); // New state for DeviceInfo recordStatus
  const [packageRecordStatus, setPackageRecordStatus] = useState('active'); // New state for Package recordStatus
  const [eddFileWarning, setEddFileWarning] = useState(null);
  const [devinfoFileWarning, setDevinfoFileWarning] = useState(null);
  const [zipValidationWarning, setZipValidationWarning] = useState(null);
  const [showZipWarningDialog, setShowZipWarningDialog] = useState(false);
  const [zipWarningType, setZipWarningType] = useState(null);
  const [canUpload, setCanUpload] = useState(false);
  const [manufacturerNamesByProtocol, setManufacturerNamesByProtocol] = useState({});
  const [deviceTypeNamesByProtocolAndManufacturer, setDeviceTypeNamesByProtocolAndManufacturer] = useState({});
  const [eddManufacturerString, setEddManufacturerString] = useState('');
  const [eddDeviceTypeString, setEddDeviceTypeString] = useState('');
  const [devinfoManufacturerString, setDevinfoManufacturerString] = useState('');
  const [devinfoDeviceTypeString, setDevinfoDeviceTypeString] = useState('');

  const eddProgressSteps = ['Idle', 'Uploading EDD'];
  const devinfoProgressSteps = ['Idle', 'Uploading DeviceInfo'];
  const uploadDelayMs = parseInt(process.env.REACT_APP_UPLOAD_DELAY_MS, 10) || 0;
  const uploadDelaySeconds = Math.round(uploadDelayMs / 1000);
  const packageProgressSteps = [
    'Idle',
    '1. Getting locator for upload',
    '2. Uploading file',
    `3. Waiting (${uploadDelaySeconds} seconds)`,
    '4. Activating',
  ];

  const getAccessToken = async () => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/access_as_user'],
        account: accounts[0],
      });
      const decoded = jwtDecode(response.accessToken);
      const roles = decoded.roles || [];
      const hasUploadRole = roles.includes('repository.contributor') || roles.includes('repository.manager');
      setCanUpload(hasUploadRole);
      return response.accessToken;
    } catch (err) {
      console.error('Token acquisition failed:', err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchManufacturerData = async () => {
      try {
        const response = await fetch('https://assets.fieldcommgroup.org/public/registry/production/id/manufacturerids.json');
        if (!response.ok) throw new Error('Failed to fetch manufacturer IDs');
        const data = await response.json();
        const manufacturerData = data.manufacturerIds || [];
        const mappingByProtocol = manufacturerData.reduce((acc, item) => {
          const protocol = item.protocol.toLowerCase();
          if (!acc[protocol]) acc[protocol] = {};
          acc[protocol][item.manufacturerId.toLowerCase()] = item.manufacturerString;
          return acc;
        }, {});
        setManufacturerNamesByProtocol(mappingByProtocol);
      } catch (err) {
        console.error('Error fetching manufacturer IDs:', err);
        setError('Failed to load manufacturer names.');
      }
    };

    const fetchDeviceTypeData = async () => {
      try {
        const response = await fetch('https://assets.fieldcommgroup.org/public/registry/production/id/devicetypes.json');
        if (!response.ok) throw new Error('Failed to fetch device type IDs');
        const data = await response.json();
        const deviceTypeData = data.deviceTypes || [];
        const mappingByProtocolAndManufacturer = deviceTypeData.reduce((acc, item) => {
          const protocol = item.protocol.toLowerCase();
          const manufacturerId = item.manufacturerId.toLowerCase();
          const deviceTypeId = item.deviceTypeId.toLowerCase();
          if (!acc[protocol]) acc[protocol] = {};
          if (!acc[protocol][manufacturerId]) acc[protocol][manufacturerId] = {};
          acc[protocol][manufacturerId][deviceTypeId] = item.deviceTypeIdString;
          return acc;
        }, {});
        setDeviceTypeNamesByProtocolAndManufacturer(mappingByProtocolAndManufacturer);
      } catch (err) {
        console.error('Error fetching device type IDs:', err);
        setError('Failed to load device type names.');
      }
    };

    fetchManufacturerData();
    fetchDeviceTypeData();
    getAccessToken();
  }, []);

  const isHex = (value) => /^[0-9A-Fa-f]*$/.test(value);

  const parseFilename = (filename) => {
    const pattern = /^([a-z]+)\.([0-9A-Fa-f]{6})\.([0-9A-Fa-f]{4})\.([0-9A-Fa-f]{2})\.zip$/i;
    const match = filename.match(pattern);
    if (match) {
      const [, protocol, manufacturerId, deviceType, deviceRevision] = match;
      return { protocol: protocol.toLowerCase(), manufacturerId, deviceType, deviceRevision };
    }
    return null;
  };

  const validateZipStructure = async (file, manufacturerId) => {
    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      const files = Object.keys(contents.files);
      const rootEntries = new Set(files.map(f => f.split('/')[0].toLowerCase()));
      const expectedRoot = manufacturerId.toLowerCase();
      if (rootEntries.size !== 1 || !rootEntries.has(expectedRoot)) {
        return `Invalid structure: Root must contain only "${manufacturerId}" (case insensitive), found: ${Array.from(files.map(f => f.split('/')[0])).join(', ')}.`;
      }
      const invalidFiles = files.filter(f => !f.toLowerCase().startsWith(`${expectedRoot}/`) || f.toLowerCase() === `${expectedRoot}/`);
      if (invalidFiles.length > 0) {
        return `Invalid structure: Files must be nested under "${manufacturerId}/" (case insensitive), found at root: ${invalidFiles.join(', ')}.`;
      }
      return null;
    } catch (err) {
      console.error('Error validating zip:', err);
      return 'Error reading zip file: ' + err.message;
    }
  };

  const handleFileChange = (event, type) => {
    const file = event.target.files[0];
    if (type === 'edd') {
      setEddFile(file);
      setEddProgress(0);
      setEddFileWarning(null);
      setZipValidationWarning(null);
      setEddManufacturerString('');
      setEddDeviceTypeString('');
      if (file) {
        const parsed = parseFilename(file.name);
        if (parsed) {
          setEddProtocol(parsed.protocol);
          setEddManufacturerId(parsed.manufacturerId);
          setEddDeviceType(parsed.deviceType);
          setEddDeviceRevision(parsed.deviceRevision);
          const protocolLower = parsed.protocol.toLowerCase();
          const manufacturerIdLower = parsed.manufacturerId.toLowerCase();
          const deviceTypeLower = parsed.deviceType.toLowerCase();
          const manufacturerMapping = manufacturerNamesByProtocol[protocolLower] || {};
          const manufacturerName = manufacturerMapping[manufacturerIdLower] || 'Unknown';
          setEddManufacturerString(manufacturerName);
          const protocolMapping = deviceTypeNamesByProtocolAndManufacturer[protocolLower] || {};
          const manufacturerDeviceTypeMapping = protocolMapping[manufacturerIdLower] || {};
          const deviceTypeName = manufacturerDeviceTypeMapping[deviceTypeLower] || 'Unknown';
          setEddDeviceTypeString(deviceTypeName);
        } else {
          setEddProtocol('');
          setEddManufacturerId('');
          setEddDeviceType('');
          setEddDeviceRevision('');
          setEddFileWarning(`Warning: File "${file.name}" does not follow the expected pattern (e.g., foundation.502b46.0005.01.zip). Please verify metadata manually.`);
        }
      }
    } else if (type === 'package') {
      setPackageFile(file);
      setPackageProgress(0);
    } else if (type === 'devinfo') {
      setDevinfoFile(file);
      setDevinfoProgress(0);
      setDevinfoFileWarning(null);
      setZipValidationWarning(null);
      setDevinfoManufacturerString('');
      setDevinfoDeviceTypeString('');
      if (file) {
        const parsed = parseFilename(file.name);
        if (parsed) {
          setDevinfoProtocol(parsed.protocol);
          setDevinfoManufacturerId(parsed.manufacturerId);
          setDevinfoDeviceType(parsed.deviceType);
          setDevinfoDeviceRevision(parsed.deviceRevision);
          const protocolLower = parsed.protocol.toLowerCase();
          const manufacturerIdLower = parsed.manufacturerId.toLowerCase();
          const deviceTypeLower = parsed.deviceType.toLowerCase();
          const manufacturerMapping = manufacturerNamesByProtocol[protocolLower] || {};
          const manufacturerName = manufacturerMapping[manufacturerIdLower] || 'Unknown';
          setDevinfoManufacturerString(manufacturerName);
          const protocolMapping = deviceTypeNamesByProtocolAndManufacturer[protocolLower] || {};
          const manufacturerDeviceTypeMapping = protocolMapping[manufacturerIdLower] || {};
          const deviceTypeName = manufacturerDeviceTypeMapping[deviceTypeLower] || 'Unknown';
          setDevinfoDeviceTypeString(deviceTypeName);
        } else {
          setDevinfoProtocol('');
          setDevinfoManufacturerId('');
          setDevinfoDeviceType('');
          setDevinfoDeviceRevision('');
          setDevinfoFileWarning(`Warning: File "${file.name}" does not follow the expected pattern (e.g., hart.502b46.0005.01.zip). Please verify metadata manually.`);
        }
      }
    }
    setError(null);
    setSuccess(null);
  };

  const handleHexInputChange = (setter, field, type) => (event) => {
    const value = event.target.value;
    if (isHex(value) || value === '') {
      setter(value);
      if (field === 'manufacturerId' && (type === 'edd' ? eddProtocol : devinfoProtocol)) {
        const protocolLower = (type === 'edd' ? eddProtocol : devinfoProtocol).toLowerCase();
        const idLower = value.toLowerCase();
        const mapping = manufacturerNamesByProtocol[protocolLower] || {};
        const manufacturerName = mapping[idLower] || 'Unknown';
        if (type === 'edd') {
          setEddManufacturerString(manufacturerName);
          if (eddDeviceType) {
            const deviceTypeLower = eddDeviceType.toLowerCase();
            const protocolMapping = deviceTypeNamesByProtocolAndManufacturer[protocolLower] || {};
            const manufacturerDeviceTypeMapping = protocolMapping[idLower] || {};
            const deviceTypeName = manufacturerDeviceTypeMapping[deviceTypeLower] || 'Unknown';
            setEddDeviceTypeString(deviceTypeName);
          }
        } else {
          setDevinfoManufacturerString(manufacturerName);
          if (devinfoDeviceType) {
            const deviceTypeLower = devinfoDeviceType.toLowerCase();
            const protocolMapping = deviceTypeNamesByProtocolAndManufacturer[protocolLower] || {};
            const manufacturerDeviceTypeMapping = protocolMapping[idLower] || {};
            const deviceTypeName = manufacturerDeviceTypeMapping[deviceTypeLower] || 'Unknown';
            setDevinfoDeviceTypeString(deviceTypeName);
          }
        }
      } else if (field === 'deviceType' && (type === 'edd' ? eddProtocol && eddManufacturerId : devinfoProtocol && devinfoManufacturerId)) {
        const protocolLower = (type === 'edd' ? eddProtocol : devinfoProtocol).toLowerCase();
        const manufacturerIdLower = (type === 'edd' ? eddManufacturerId : devinfoManufacturerId).toLowerCase();
        const deviceTypeLower = value.toLowerCase();
        const protocolMapping = deviceTypeNamesByProtocolAndManufacturer[protocolLower] || {};
        const manufacturerDeviceTypeMapping = protocolMapping[manufacturerIdLower] || {};
        const deviceTypeName = manufacturerDeviceTypeMapping[deviceTypeLower] || 'Unknown';
        if (type === 'edd') {
          setEddDeviceTypeString(deviceTypeName);
        } else {
          setDevinfoDeviceTypeString(deviceTypeName);
        }
      }
    }
  };

  const handleEddUpload = async () => {
    if (!canUpload) {
      setError('You do not have sufficient permissions to upload to the repository.');
      return;
    }

    if (!eddFile) {
      setError('Please select an EDD file to upload.');
      return;
    }
    if (!eddProtocol || !eddManufacturerId || !eddDeviceType || !eddDeviceRevision) {
      setError('Please provide all EDD metadata: protocol, manufacturer ID, device type, and device revision.');
      return;
    }

    setEddLoading(true);
    setError(null);
    setSuccess(null);
    setEddProgress(1);

    try {
      const fileBuffer = await eddFile.arrayBuffer();
      console.log(`Client: Original file buffer size: ${fileBuffer.byteLength} bytes`);
      const validationBlob = new Blob([fileBuffer]);
      const validationResult = await validateZipStructure(validationBlob, eddManufacturerId);
      if (validationResult) {
        setZipValidationWarning(validationResult);
        setShowZipWarningDialog(true);
        setZipWarningType('edd');
        setEddLoading(false);
        setEddProgress(0);
        return;
      }

      const uploadBlob = new Blob([fileBuffer]);
      console.log(`Client: Upload blob size before sending: ${uploadBlob.size} bytes`);
      await proceedWithEddUpload(uploadBlob);
    } catch (err) {
      console.error('EDD upload error:', err);
      setError(`Failed to upload EDD: ${err.message}`);
      setEddLoading(false);
      setEddProgress(0);
    }
  };

  const proceedWithEddUpload = async (uploadBlob) => {
    setEddLoading(true);
    setError(null);
    setSuccess(null);
    setEddProgress(1);

    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append('files', uploadBlob, eddFile.name);

      const url = `${apiBaseUrl}/edds/protocol/${encodeURIComponent(eddProtocol)}/manufacturerId/${encodeURIComponent(eddManufacturerId)}/deviceType/${encodeURIComponent(eddDeviceType)}/deviceRevision/${encodeURIComponent(eddDeviceRevision)}/media?recordStatus=${encodeURIComponent(eddRecordStatus)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.fault?.faultstring || response.statusText);
      }

      const responseData = await response.json();
      setSuccess('EDD uploaded successfully!');
    } catch (err) {
      console.error('EDD upload error:', { message: err.message });
      setError(`Failed to upload EDD: ${err.message}`);
    } finally {
      setEddLoading(false);
      setEddProgress(0);
    }
  };

  const handleDevinfoUpload = async () => {
    if (!canUpload) {
      setError('You do not have sufficient permissions to upload to the repository.');
      return;
    }

    if (!devinfoFile) {
      setError('Please select a DeviceInfo file to upload.');
      return;
    }
    if (!devinfoProtocol || !devinfoManufacturerId || !devinfoDeviceType || !devinfoDeviceRevision) {
      setError('Please provide all DeviceInfo metadata: protocol, manufacturer ID, device type, and device revision.');
      return;
    }

    setDevinfoLoading(true);
    setError(null);
    setSuccess(null);
    setDevinfoProgress(1);

    try {
      const fileBuffer = await devinfoFile.arrayBuffer();
      console.log(`Client: Original file buffer size: ${fileBuffer.byteLength} bytes`);
      const validationBlob = new Blob([fileBuffer]);
      const validationResult = await validateZipStructure(validationBlob, devinfoManufacturerId);
      if (validationResult) {
        setZipValidationWarning(validationResult);
        setShowZipWarningDialog(true);
        setZipWarningType('devinfo');
        setDevinfoLoading(false);
        setDevinfoProgress(0);
        return;
      }

      const uploadBlob = new Blob([fileBuffer]);
      console.log(`Client: Upload blob size before sending: ${uploadBlob.size} bytes`);
      await proceedWithDevinfoUpload(uploadBlob);
    } catch (err) {
      console.error('DeviceInfo upload error:', err);
      setError(`Failed to upload DeviceInfo: ${err.message}`);
      setDevinfoLoading(false);
      setDevinfoProgress(0);
    }
  };

  const proceedWithDevinfoUpload = async (uploadBlob) => {
    setDevinfoLoading(true);
    setError(null);
    setSuccess(null);
    setDevinfoProgress(1);

    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append('file', uploadBlob, devinfoFile.name);

      const url = `${apiBaseUrl}/devinfos/protocol/${encodeURIComponent(devinfoProtocol)}/manufacturerId/${encodeURIComponent(devinfoManufacturerId)}/deviceType/${encodeURIComponent(devinfoDeviceType)}/deviceRevision/${encodeURIComponent(devinfoDeviceRevision)}/media?recordStatus=${encodeURIComponent(devinfoRecordStatus)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.fault?.faultstring || response.statusText);
      }

      setSuccess('DeviceInfo uploaded successfully!');
    } catch (err) {
      console.error('DeviceInfo upload error:', { message: err.message });
      setError(`Failed to upload DeviceInfo: ${err.message}`);
    } finally {
      setDevinfoLoading(false);
      setDevinfoProgress(0);
    }
  };

  const handleZipWarningContinue = () => {
    setShowZipWarningDialog(false);
    if (zipWarningType === 'edd') {
      proceedWithEddUpload(new Blob([eddFile]));
    } else if (zipWarningType === 'devinfo') {
      proceedWithDevinfoUpload(new Blob([devinfoFile]));
    }
  };

  const handleZipWarningCancel = () => {
    setShowZipWarningDialog(false);
    if (zipWarningType === 'edd') {
      setEddLoading(false);
      setEddProgress(0);
      setError('EDD upload cancelled due to invalid zip structure.');
    } else if (zipWarningType === 'devinfo') {
      setDevinfoLoading(false);
      setDevinfoProgress(0);
      setError('DeviceInfo upload cancelled due to invalid zip structure.');
    }
    setZipWarningType(null);
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handlePackageUpload = async () => {
    if (!canUpload) {
      setError('You do not have sufficient permissions to upload to the repository.');
      return;
    }

    if (!packageFile) {
      setError('Please select a Package file to upload.');
      return;
    }

    setPackageLoading(true);
    setError(null);
    setSuccess(null);
    setPackageProgress(1);

    try {
      const token = await getAccessToken();
      setPackageProgress(1);
      const locatorUrl = `${apiBaseUrl}/packages/locators?fileName=${encodeURIComponent(packageFile.name)}`;
      const locatorResponse = await fetch(locatorUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!locatorResponse.ok) {
        const errorData = await locatorResponse.json();
        throw new Error(errorData.fault?.faultstring || locatorResponse.statusText);
      }

      const locatorData = await locatorResponse.json();
      const { id: locatorId, url: uploadUrl } = locatorData;

      setPackageProgress(2);
      const blobResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': packageFile.type,
          'x-ms-blob-type': 'BlockBlob',
        },
        body: packageFile,
      });

      if (!blobResponse.ok) {
        throw new Error(blobResponse.statusText);
      }

      if (uploadDelayMs > 0) {
        setPackageProgress(3);
        await delay(uploadDelayMs);
      }

      setPackageProgress(4);
      const confirmUrl = `${apiBaseUrl}/packages/locator/${locatorId}?recordStatus=${encodeURIComponent(packageRecordStatus)}`;
      const confirmResponse = await fetch(confirmUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseBody = await confirmResponse.text();
      const contentType = confirmResponse.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const isEmptyBody = responseBody.trim() === '';

      if (!confirmResponse.ok) {
        let errorMessage = confirmResponse.statusText;
        try {
          if (isJson && !isEmptyBody) {
            const errorData = JSON.parse(responseBody);
            errorMessage = errorData.fault?.faultstring || confirmResponse.statusText;
          } else {
            errorMessage = responseBody || confirmResponse.statusText;
          }
        } catch (err) {
          errorMessage = responseBody || confirmResponse.statusText;
        }
        throw new Error(errorMessage);
      }

      setSuccess('Package uploaded successfully!');
      setPackageProgress(0);
    } catch (err) {
      console.error('Package upload error:', { message: err.message });
      setError(`Failed to upload Package: ${err.message}`);
      setPackageProgress(0);
    } finally {
      setPackageLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: 600 }}>
      <Typography variant="h4" gutterBottom>
        Uploader
      </Typography>

      {apiBaseUrl === 'https://fdirepoproxy.azurewebsites.net/repository/v1' && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          <AlertTitle>WARNING: Production Environment</AlertTitle>
          You are uploading to the PRODUCTION database. Changes here are permanent and affect live data. Proceed with caution!
        </Alert>
      )}

      {!canUpload && (
        <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
          <AlertTitle>Insufficient Permissions</AlertTitle>
          You do not have sufficient permissions to upload to the repository. You must have the role <strong>repository.contributor</strong> or <strong>repository.manager</strong>.
        </Alert>
      )}

      {/* EDD Upload Section */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="h6">Upload EDD</Typography>
        <TextField
          type="file"
          inputProps={{ accept: '.zip' }}
          onChange={(e) => handleFileChange(e, 'edd')}
          sx={{ mt: 2, mb: 1, width: 600 }}
          disabled={eddLoading || !canUpload}
        />
        {eddFileWarning && (
          <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
            {eddFileWarning}
          </Typography>
        )}
        <FormControl sx={{ mb: 1, width: 600 }}>
          <InputLabel>Protocol</InputLabel>
          <Select
            value={eddProtocol}
            onChange={(e) => setEddProtocol(e.target.value)}
            disabled={eddLoading || !canUpload}
          >
            <MenuItem value=""><em>Select Protocol</em></MenuItem>
            <MenuItem value="hart">hart</MenuItem>
            <MenuItem value="foundation">foundation</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <TextField
            label="Manufacturer ID (6 hex digits, e.g., 502b46)"
            value={eddManufacturerId}
            onChange={handleHexInputChange(setEddManufacturerId, 'manufacturerId', 'edd')}
            sx={{ width: 600 }}
            disabled={eddLoading || !canUpload}
            inputProps={{ maxLength: 6 }}
          />
          {eddManufacturerString && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {eddManufacturerString}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <TextField
            label="Device Type (4 hex digits, e.g., 0005)"
            value={eddDeviceType}
            onChange={handleHexInputChange(setEddDeviceType, 'deviceType', 'edd')}
            sx={{ width: 600 }}
            disabled={eddLoading || !canUpload}
            inputProps={{ maxLength: 4 }}
          />
          {eddDeviceTypeString && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {eddDeviceTypeString}
            </Typography>
          )}
        </Box>
        <TextField
          label="Device Revision (2 hex digits, e.g., 01)"
          value={eddDeviceRevision}
          onChange={handleHexInputChange(setEddDeviceRevision, undefined, 'edd')}
          sx={{ mb: 1, width: 600 }}
          disabled={eddLoading || !canUpload}
          inputProps={{ maxLength: 2 }}
        />
        <FormControl sx={{ mb: 1, width: 600 }}>
          <InputLabel>Record Status</InputLabel>
          <Select
            value={eddRecordStatus}
            onChange={(e) => setEddRecordStatus(e.target.value)}
            disabled={eddLoading || !canUpload}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        {eddLoading && eddProgress > 0 && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {eddProgressSteps[eddProgress]}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleEddUpload}
          disabled={eddLoading || !eddFile || !canUpload}
          sx={{ alignSelf: 'flex-start' }}
        >
          Upload EDD
        </Button>
      </Box>

      {/* DeviceInfo Upload Section */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="h6">Upload DeviceInfos</Typography>
        <TextField
          type="file"
          inputProps={{ accept: '.zip' }}
          onChange={(e) => handleFileChange(e, 'devinfo')}
          sx={{ mt: 2, mb: 1, width: 600 }}
          disabled={devinfoLoading || !canUpload}
        />
        {devinfoFileWarning && (
          <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
            {devinfoFileWarning}
          </Typography>
        )}
        <FormControl sx={{ mb: 1, width: 600 }}>
          <InputLabel>Protocol</InputLabel>
          <Select
            value={devinfoProtocol}
            onChange={(e) => setDevinfoProtocol(e.target.value)}
            disabled={devinfoLoading || !canUpload}
          >
            <MenuItem value=""><em>Select Protocol</em></MenuItem>
            <MenuItem value="hart">hart</MenuItem>
            <MenuItem value="foundation">foundation</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <TextField
            label="Manufacturer ID (6 hex digits, e.g., 502b46)"
            value={devinfoManufacturerId}
            onChange={handleHexInputChange(setDevinfoManufacturerId, 'manufacturerId', 'devinfo')}
            sx={{ width: 600 }}
            disabled={devinfoLoading || !canUpload}
            inputProps={{ maxLength: 6 }}
          />
          {devinfoManufacturerString && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {devinfoManufacturerString}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <TextField
            label="Device Type (4 hex digits, e.g., 0005)"
            value={devinfoDeviceType}
            onChange={handleHexInputChange(setDevinfoDeviceType, 'deviceType', 'devinfo')}
            sx={{ width: 600 }}
            disabled={devinfoLoading || !canUpload}
            inputProps={{ maxLength: 4 }}
          />
          {devinfoDeviceTypeString && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {devinfoDeviceTypeString}
            </Typography>
          )}
        </Box>
        <TextField
          label="Device Revision (2 hex digits, e.g., 01)"
          value={devinfoDeviceRevision}
          onChange={handleHexInputChange(setDevinfoDeviceRevision, undefined, 'devinfo')}
          sx={{ mb: 1, width: 600 }}
          disabled={devinfoLoading || !canUpload}
          inputProps={{ maxLength: 2 }}
        />
        <FormControl sx={{ mb: 1, width: 600 }}>
          <InputLabel>Record Status</InputLabel>
          <Select
            value={devinfoRecordStatus}
            onChange={(e) => setDevinfoRecordStatus(e.target.value)}
            disabled={devinfoLoading || !canUpload}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        {devinfoLoading && devinfoProgress > 0 && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {devinfoProgressSteps[devinfoProgress]}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleDevinfoUpload}
          disabled={devinfoLoading || !devinfoFile || !canUpload}
          sx={{ alignSelf: 'flex-start' }}
        >
          Upload DeviceInfo
        </Button>
      </Box>

      {/* Package Upload Section */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="h6">Upload Package</Typography>
        <TextField
          type="file"
          inputProps={{ accept: '.fdix' }}
          onChange={(e) => handleFileChange(e, 'package')}
          sx={{ mt: 2, mb: 1, width: 600 }}
          disabled={packageLoading || !canUpload}
        />
        <FormControl sx={{ mb: 1, width: 600 }}>
          <InputLabel>Record Status</InputLabel>
          <Select
            value={packageRecordStatus}
            onChange={(e) => setPackageRecordStatus(e.target.value)}
            disabled={packageLoading || !canUpload}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        {packageLoading && packageProgress > 0 && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {packageProgressSteps[packageProgress]}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handlePackageUpload}
          disabled={packageLoading || !packageFile || !canUpload}
          sx={{ alignSelf: 'flex-start' }}
        >
          Upload Package
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
      {success && (
        <Typography color="success.main" sx={{ mt: 2 }}>
          {success}
        </Typography>
      )}

      <Dialog
        open={showZipWarningDialog}
        onClose={() => setShowZipWarningDialog(false)}
        aria-labelledby="zip-warning-title"
        aria-describedby="zip-warning-description"
      >
        <DialogTitle id="zip-warning-title">{zipWarningType === 'edd' ? 'EDD' : 'DeviceInfo'} Zip Structure Warning</DialogTitle>
        <DialogContent>
          <DialogContentText id="zip-warning-description">
            {zipValidationWarning} Do you want to proceed with the upload anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleZipWarningCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleZipWarningContinue} color="warning">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FdiRepositoryUploader;